import type { ToolId } from './tools.js';

/**
 * The three stages of making a building.
 *
 * The app always had this order — draw each wall's elevation, arrange the
 * walls into a footprint, then look at the result — but nothing on screen said
 * so. The tools were a flat rail, the plan was one tool among seven, and 3D
 * was a button in the corner, so the sequence had to be inferred.
 *
 * A stage is DERIVED, never stored. Two sources of truth for "where am I" is
 * the same mistake as the plan's old separate wall selection: they drift. The
 * tool and the 3D flag already say everything, so this reads them.
 */
export type StageId = 'draw' | 'arrange' | 'view';

export const STAGE_ORDER: readonly StageId[] = ['draw', 'arrange', 'view'];

/** The tools each stage offers in the rail, in rail order. */
export const STAGE_TOOLS: Record<StageId, readonly ToolId[]> = {
  draw: ['select', 'sketch', 'build', 'paint', 'measure', 'pan'],
  arrange: ['plan', 'pan'],
  // The 3D view is an overlay with its own controls and no rail of its own.
  view: [],
};

/** The tool a stage opens with when it is entered from the step bar. */
export const STAGE_DEFAULT_TOOL: Record<StageId, ToolId> = {
  draw: 'build',
  arrange: 'plan',
  view: 'plan',
};

export const STAGE_LABEL: Record<StageId, string> = {
  draw: 'Draw walls',
  arrange: 'Arrange plan',
  view: 'View in 3D',
};

/**
 * Which stage a tool belongs to.
 *
 * Pan is deliberately absent: it belongs to whichever stage you were already
 * in. Moving the view is not a change of intent, and being thrown out of the
 * plan for reaching for the hand tool would be its own small betrayal.
 */
export function stageOfTool(tool: ToolId, previousTool: ToolId): StageId {
  if (tool === 'plan') return 'arrange';
  if (tool === 'pan') return previousTool === 'plan' ? 'arrange' : 'draw';
  return 'draw';
}

/** The stage the whole editor is in, 3D included. */
export function currentStage(state: {
  readonly tool: ToolId;
  readonly previousTool: ToolId;
  readonly view3d: boolean;
}): StageId {
  return state.view3d ? 'view' : stageOfTool(state.tool, state.previousTool);
}
